import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import './SearchAutocomplete.css';

interface Suggestion {
    // code_article (V2) renvoie un id ES composite (chaîne) ; les autres types un id MySQL entier.
    id: string | number;
    title: string;
    title_highlighted?: string;
    subtitle?: string;
    text_preview?: string;
    excerpt?: string;
    date?: string;
    type: string;
}

interface SuggestionGroup {
    type: string;
    label: string;
    icon: string;
    search_legend?: string;
    suggestions: Suggestion[];
    total: number;
}

interface SearchAutocompleteProps {
    docType?: string;
    globalMode?: boolean;
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    onShowSuggestionsChange?: (show: boolean) => void;
    onLoadingChange?: (loading: boolean) => void;
    placeholder?: string;
}

const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
    docType,
    globalMode = false,
    value,
    onChange,
    onSubmit,
    onShowSuggestionsChange,
    onLoadingChange,
    placeholder
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [groups, setGroups] = useState<SuggestionGroup[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [searchLegend, setSearchLegend] = useState<string>(''); // For single docType mode
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    // Map doc types to their display info
    const docTypeInfo: Record<string, { icon: string; label: string }> = {
        code_article: { icon: 'bi-book', label: t('search.types.code_article', 'Article') },
        jps: { icon: 'bi-hammer', label: t('search.types.jps', 'Arrêt') },
        joarticles: { icon: 'bi-file-text', label: t('search.types.jo', 'Texte') },
        theses: { icon: 'bi-mortarboard', label: t('search.types.these', 'Thèse') },
        livres: { icon: 'bi-book-half', label: t('search.types.livre', 'Livre') },
    };

    // Detail routes for navigation
    const detailRoutes: Record<string, string> = {
        code_article: 'detail/code-article',
        jps: 'detail/jurisprudence',
        joarticles: 'detail/jo',
        theses: 'detail/theses',
        livres: 'detail/livres'
    };

    // Fetch suggestions with debounce
    const fetchSuggestions = useCallback(async (query: string) => {
        if (query.length < 2) {
            setSuggestions([]);
            setGroups([]);
            setShowSuggestions(false);
            return;
        }

        setIsLoading(true);
        try {
            if (globalMode) {
                // Global mode: fetch from all categories
                const response = await api.post('/search/autocomplete-global', {
                    query: query,
                    lang: i18n.language,
                    limit: 3
                });
                const resultGroups = response.data?.groups || [];
                const count = response.data?.total_count || 0;
                setGroups(resultGroups);
                setTotalCount(count);
                // Flatten for keyboard navigation
                const flatSuggestions = resultGroups.flatMap((g: SuggestionGroup) => g.suggestions);
                setSuggestions(flatSuggestions);
                setShowSuggestions(flatSuggestions.length > 0);
            } else {
                // Single doc_type mode (existing behavior)
                const response = await api.post('/search/autocomplete', {
                    query: query,
                    doc_type: docType,
                    lang: i18n.language,
                    limit: 6
                });
                const results = response.data?.suggestions || [];
                const count = response.data?.total_count || results.length;
                const legend = response.data?.search_legend || '';
                setSuggestions(results);
                setTotalCount(count);
                setSearchLegend(legend);
                setShowSuggestions(results.length > 0);
            }
        } catch (error) {
            console.error('Autocomplete error:', error);
            setSuggestions([]);
            setGroups([]);
        } finally {
            setIsLoading(false);
        }
    }, [docType, globalMode, i18n.language]);

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(value);
        }, 300);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [value, fetchSuggestions]);

    // Notify parent when suggestions visibility changes
    useEffect(() => {
        onShowSuggestionsChange?.(showSuggestions);
    }, [showSuggestions, onShowSuggestionsChange]);

    // Notify parent when loading state changes
    useEffect(() => {
        onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions) {
            if (e.key === 'Enter') {
                onSubmit();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                    handleSuggestionClick(suggestions[selectedIndex]);
                } else {
                    onSubmit();
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedIndex(-1);
                break;
        }
    };

    // Handle suggestion click (navigate to details immediately)
    const handleSuggestionClick = (suggestion: Suggestion) => {
        setShowSuggestions(false);
        const route = detailRoutes[suggestion.type] || 'detail/code-article';

        const domainMap: Record<string, string> = {
            code_article: 'code_articles',
            jps: 'jurisprudences',
            joarticles: 'joarticles',
            theses: 'theses',
            livres: 'livres'
        };
        const domain = domainMap[suggestion.type] || 'code_articles';

        const params = new URLSearchParams();
        if (value.trim()) {
            params.set('q', value.trim());
        }
        params.set('domain', domain);

        navigate(`/${i18n.language}/${route}/${suggestion.id}?${params.toString()}`);
    };

    // Render a single suggestion item
    const renderSuggestionItem = (suggestion: Suggestion, flatIndex: number) => {
        const info = docTypeInfo[suggestion.type] || docTypeInfo.code_article;
        return (
            <li
                key={`${suggestion.type}-${suggestion.id}`}
                className={`suggestion-item ${flatIndex === selectedIndex ? 'selected' : ''}`}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSuggestionClick(suggestion);
                }}
                onMouseEnter={() => setSelectedIndex(flatIndex)}
            >
                <div className="suggestion-icon">
                    <i className={`bi ${info.icon}`}></i>
                </div>
                <div className="suggestion-content">
                    <div className="suggestion-title">
                        {suggestion.type === 'code_article' ? (
                            <>
                                <strong>{suggestion.title} - {suggestion.subtitle}</strong>
                                {suggestion.title_highlighted && (
                                    <div className="suggestion-subtitle mt-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(suggestion.title_highlighted) }} />
                                )}
                            </>
                        ) : (
                            <>
                                {suggestion.title_highlighted ? (
                                    <strong dangerouslySetInnerHTML={{ __html: sanitizeHtml(suggestion.title_highlighted) }} />
                                ) : (
                                    <strong>{suggestion.title}</strong>
                                )}
                                {suggestion.subtitle && (
                                    <div className="suggestion-subtitle" dangerouslySetInnerHTML={{ __html: sanitizeHtml(suggestion.subtitle) }} />
                                )}
                            </>
                        )}
                    </div>
                    {suggestion.text_preview && (
                        <div
                            className="suggestion-excerpt preview-text"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(suggestion.text_preview) }}
                        />
                    )}
                    {suggestion.excerpt && !suggestion.text_preview && (
                        <div
                            className="suggestion-excerpt"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(suggestion.excerpt) }}
                        />
                    )}
                </div>
                {suggestion.date && (
                    <div className="suggestion-date">
                        <i className="bi bi-calendar3"></i>
                        <span>{suggestion.date}</span>
                    </div>
                )}
                <div className="suggestion-arrow">
                    <i className="bi bi-chevron-right"></i>
                </div>
            </li>
        );
    };

    return (
        <div className="search-autocomplete" ref={containerRef}>
            <div className="autocomplete-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input-large"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setSelectedIndex(-1);
                    }}
                    onFocus={() => value.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                />
            </div>

            {showSuggestions && suggestions.length > 0 && (
                <div className="autocomplete-dropdown">
                    {globalMode && groups.length > 0 ? (
                        /* Global mode: grouped by category */
                        <>
                            <div className="autocomplete-stats">
                                <i className="bi bi-search"></i>
                                <span>
                                    {totalCount} {i18n.language === 'ar' ? 'نتيجة' : 'résultat(s)'}
                                </span>
                            </div>
                            <ul className="suggestions-list suggestions-list-global">
                                {(() => {
                                    let flatIndex = 0;
                                    return groups.map((group) => (
                                        <React.Fragment key={group.type}>
                                            <li className="suggestion-group-header">
                                                <div className="suggestion-group-title">
                                                    <i className={`bi ${group.icon}`}></i>
                                                    <span>{group.label}</span>
                                                    <span className="suggestion-group-count">{group.total}</span>
                                                </div>
                                                {group.search_legend && (
                                                    <div className="suggestion-group-legend">{group.search_legend}</div>
                                                )}
                                            </li>
                                            {group.suggestions.map((suggestion) => {
                                                const item = renderSuggestionItem(suggestion, flatIndex);
                                                flatIndex++;
                                                return item;
                                            })}
                                        </React.Fragment>
                                    ));
                                })()}
                            </ul>
                        </>
                    ) : (
                        /* Single doc_type mode */
                        <>
                            <div className="autocomplete-stats">
                                <i className={`bi ${docTypeInfo[docType || 'code_article']?.icon || 'bi-search'}`}></i>
                                <span>
                                    {totalCount} {i18n.language === 'ar' ? 'نتيجة' : 'résultat(s)'}
                                </span>
                            </div>
                            {searchLegend && (
                                <div className="suggestion-group-legend standalone">{searchLegend}</div>
                            )}
                            <ul className="suggestions-list">
                                {suggestions.map((suggestion, index) =>
                                    renderSuggestionItem(suggestion, index)
                                )}
                            </ul>
                        </>
                    )}

                    <div className="dropdown-footer" onClick={onSubmit}>
                        <i className="bi bi-search"></i>
                        <span>{i18n.language === 'ar' ? 'عرض كل النتائج' : 'Voir tous les résultats'}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchAutocomplete;
